import { Component, Input, Output, EventEmitter, OnInit, AfterViewChecked, HostListener } from '@angular/core';
import { from, map, mergeMap, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, groupBy, toArray } from 'rxjs/operators';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ModalService } from '../../../core/services/modal.service';
import { SettingsService } from '../../../core/services/data/settings.service';
import { PurchaseProcessService } from '../../../core/services/data/purchase-process.service';
import { SiteAdvertsService } from '../../../core/services/data/site-adverts.service';
import { MemberRoleService } from '../../../core/services/data/member-roles.service';

import { ISite, ISiteAdvert } from '../../../types/datatypes';

@Component({
	selector: 'app-features-step2',
	templateUrl: './features-step2.component.html',
	styleUrls: ['./features-step2.component.scss'],
})

export class FeaturesStep2Component implements OnInit, AfterViewChecked {
	public siteAdvertsSpecific: Observable<{ title: string; content: ISiteAdvert[] }[]>;

	public memberRoles;

	public loading = true;

	@Input() isFBAdCampaign;

	@Output() openModalRequested = new EventEmitter();

	public displayFreeGuestPriceColumn = false;

	public displayGoldPlatinumPriceColumn = false;

	public siteAdverts: ISiteAdvert[];

	public text = '';

	public subText = `To access this promotion you will need to upgrade to one of 
	our paid membership plans. You can learn more or upgrade using the link below`;

	public innerWidth: any;

	public variant = 'control';

	private visibleGenres = new Map();

	constructor(
		private as: AnalyticsService,
		private ms: ModalService,
		private ss: SettingsService,
		private memberRoleService: MemberRoleService,
		private siteAdvertsService: SiteAdvertsService,
		private purchaseProcessService: PurchaseProcessService,
		private route: ActivatedRoute,
		private router: Router,
	) { }

	get siteSlug(): string {
		return this.purchaseProcessService.siteSlug;
	}

	get site(): ISite {
		return this.purchaseProcessService.site;
	}

	get authorLink(): string {
		return this.purchaseProcessService.authorLink;
	}

	get isMember(): boolean {
		return !!this.authorLink;
	}

	get role() {
		return this.purchaseProcessService.role;
	}

	ngOnInit(): void {
		this.siteAdvertsService.siteAdvertsStore$.subscribe((data) => {
			const { _id } = this.site;
			const _siteAdverts = from(data);
			this.siteAdvertsSpecific = _siteAdverts
				.pipe(
					filter((siteAdvert) => siteAdvert.isPublic && siteAdvert._site === _id),
					toArray(),
					mergeMap((siteAdverts) => {
						const _list = siteAdverts.sort((a, b) => a.emailOrder - b.emailOrder);
						this.siteAdverts = _list;
						this.handleViewList(_list);
						return from(_list);
					}),
					groupBy((siteAdvert) => siteAdvert.parentGenre),
					mergeMap((group) => group.pipe(toArray(), map((siteAdvert) => ({ title: group.key, content: siteAdvert })))),
					toArray(),
				);
		});

		this.memberRoleService.fetchMemberRoles().subscribe((data: any) => {
			this.memberRoles = data && data.success ? data.roles : [];
			this.loading = false;
		});

		this.ss.priceForGoldPlatinumStep2Setting().then(
			(value) => {
				this.displayGoldPlatinumPriceColumn = !!value;
			},
		);

		this.ss.priceForFreeGuestStep2Setting().then(
			(value) => {
				this.displayFreeGuestPriceColumn = !!value;
			},
		);

		this.innerWidth = window.innerWidth;
		this.resetScroll();
	}

	ngAfterViewChecked() {
		// after component is attached and site adverts are retrieved, trigger GTM impression event
		this.handleViewImpressions();
	}

	handleViewList(siteAdverts) {
		const analyticsAdverts = siteAdverts.map(({ seoSlug, name, price, parentGenre }) => this.as.generateAnalyticsEcommerceObject(
			seoSlug, name, this.siteSlug, parentGenre, price,
		));
		this.as.trackViewItemList(analyticsAdverts, this.siteSlug);
	}

	handleViewImpressions() {
		if (this.siteAdverts && this.siteAdverts.length) {
			let visibleGenresUpdate = false;
			for (let i = 0; i < this.siteAdverts.length; i++) {
				const siteAdvert = this.siteAdverts[i];
				const elm = document.getElementById(siteAdvert.seoSlug);
				// if siteAdvert element exists on this page get its position
				if (elm) {
					const elmPosition = elm.getBoundingClientRect();
					const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
					const windowWidth = (window.innerWidth || document.documentElement.clientWidth);

					const isInVisibleGenres = this.visibleGenres.has(siteAdvert.seoSlug);
					// Check if element is in viewport
					if (
						elmPosition.top >= 0
            && elmPosition.left >= 0
            && elmPosition.right <= windowWidth
            && elmPosition.bottom <= windowHeight
					) {
						if (!isInVisibleGenres) {
							// set site advert slug to map to its name and position in the list for GTM event
							this.visibleGenres.set(siteAdvert.seoSlug, { name: siteAdvert.name, position: i, sent: false });
							visibleGenresUpdate = true;
						}
					}
				}
			}
			if (visibleGenresUpdate) {
				// trigger impressionView event
				const user = this.purchaseProcessService.user ? this.purchaseProcessService.user._id : '';
				this.as.trackAdvertImpressionView(this.visibleGenres, this.site.name, user);
				this.visibleGenres.forEach((value) => {
					value.sent = true;
				});
			}
		}
	}

	roleNames(availableTo) {
		const roleNames = this.memberRoles
			.filter((role) => availableTo.includes(role._id))
			.map((role) => role?.roleName);
		let availableToNames = '';
		for (const [index, role] of roleNames.entries()) {
			availableToNames += role;
			if (index !== roleNames.length - 1) availableToNames += ', ';
		}
		return availableToNames;
	}

	handleLink(event, value, group) {
		if (!this.roleNames(value.availableTo).includes('Free') && !this.roleNames(value.availableTo).includes(this.role)) {
			this.text = `${value.name} is available to our Gold and Platinum Members`;
			this.ms.open('modal-promo');
			return;
		}

		// This is recommended by the Google Tag Manager team to clear the previous ecommerce object.
		// Understanding the bug may result in a better solution. Reference: https://writtenwordmedia.atlassian.net/browse/WWM-2524
		this.as.clearEcommerce(); // Clear the previous ecommerce object.

		const ecommerceItem = this.as.generateAnalyticsEcommerceObject(value.seoSlug, value.name, this.site.name, group.title, value.price);

		this.as.trackSelectItem(this.siteSlug, [ecommerceItem]);

		event.preventDefault();

		this.router.navigate([value.seoSlug], { relativeTo: this.route });
	}

	onOpenModalRequested() {
		this.openModalRequested.emit(true);
	}

	priceForGoldPlatinumMembers(price) {
		// 10% off
		const discountedPrice = price * 0.9;
		const rounded = Math.round(discountedPrice * 10) / 10;
		return this.innerWidth > 768 ? rounded.toFixed(2) : rounded.toFixed(0);
	}

	showGoldPlatinumDiscount(isMember, displayGoldPlatinumPriceColumn, displayFreeGuestPriceColumn) {
		return ((['Platinum', 'Gold'].includes(this.purchaseProcessService.role) && displayGoldPlatinumPriceColumn)
		|| ((this.purchaseProcessService.role === 'Free' || !isMember) && displayFreeGuestPriceColumn)) && this.mobileOptimization();
	}

	upgrade(event) {
		const url = window.location.pathname;
		localStorage.setItem('url', url);
		if (event) {
			this.ms.open('update-card-form');
			return;
		}
		if (!this.authorLink) {
			window.open('https://www.writtenwordmedia.com/membership/', '_blank');
			return;
		}

		this.router.navigate([`/author-dashboard/${this.authorLink}/subscriptions`]).then();
	}

	get subscriptionsLink() {
		if (!this.authorLink) return 'https://www.writtenwordmedia.com/membership/';
		return `/author-dashboard/${this.authorLink}/subscriptions`;
	}

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.innerWidth = window.innerWidth;
	}

	formatAudience(audience) {
		return this.mobileOptimization() ? audience : audience.replace(/,000/g, 'k');
	}

	mobileOptimization() {
		return this.innerWidth > 768;
	}

	resetScroll() {
		window.scrollTo(0, 0);
	}
}
